/**
 * PackageBuilder — create or edit a form package.
 *
 * Rendered full-screen (replacing the Forms tabs) like FormBuilderPage.
 *
 * Features:
 *   - Name, description
 *   - Form picker: check a form to add it to an ORDERED selected list
 *     with ↑/↓ reorder + ✕ remove (plain buttons, no drag libs)
 *   - isPublic checkbox → publicSlug input + authMode select (otp_email/otp_phone only)
 *   - Save (create or update) → onSaved
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  forms as formsApi, packages as packagesApi, FormSchema, PackageSummary, IdentityFieldDef,
} from '../../../lib/api.js';
import { sanitizeSlug, moveItem, removeAt } from '../../../lib/package-builder.js';
import {
  IDENTITY_FIELD_CATALOG, DEFAULT_IDENTITY_CONFIG,
  isIncluded, toggleField, setRequired, isDefaultConfig,
} from '../../../lib/identity-config.js';

interface Props {
  editingPackage?: PackageSummary | null | undefined;
  onSaved:  () => void;
  onCancel: () => void;
}

const AUTH_MODES = [
  { value: 'otp_phone', label: 'OTP via Phone' },
  { value: 'otp_email', label: 'OTP via Email' },
];

const MAX_FORMS = 20;

export default function PackageBuilder({ editingPackage, onSaved, onCancel }: Props): React.ReactElement {
  const [name,        setName]     = useState(editingPackage?.name ?? '');
  const [description, setDesc]     = useState(editingPackage?.description ?? '');
  const [isPublic,    setIsPublic] = useState(editingPackage?.isPublic ?? false);
  const [publicSlug,  setSlug]     = useState(editingPackage?.publicSlug ?? '');
  const [authMode,    setAuthMode] = useState(
    editingPackage?.authMode === 'otp_email' ? 'otp_email' : 'otp_phone',
  );
  const [selected,    setSelected] = useState<string[]>([]);   // ordered formIds
  // Patient-identity config (the package config wins over member forms during a
  // run). Absent (old API) → DEFAULT trio so the builder round-trips; [] = no step.
  const [identityFields, setIdentityFields] = useState<IdentityFieldDef[]>(
    editingPackage?.identityFields ? [...editingPackage.identityFields] : [...DEFAULT_IDENTITY_CONFIG],
  );

  const [allForms,  setAllForms]  = useState<FormSchema[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Load the form library (picker source) and, when editing, the package's
  // ordered items to seed the selected list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadError(null);
      try {
        const listRes = await formsApi.schemas.list();
        if (!cancelled) {
          if (listRes.success && listRes.data) setAllForms(listRes.data);
          else setLoadError(listRes.error ?? 'Failed to load forms');
        }
        if (editingPackage) {
          const detail = await packagesApi.get(editingPackage.id);
          if (!cancelled && detail.success && detail.data) {
            const ordered = [...detail.data.items]
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((i) => i.formId);
            setSelected(ordered);
            if (detail.data.identityFields) setIdentityFields([...detail.data.identityFields]);
          }
        }
      } catch {
        if (!cancelled) setLoadError('Failed to load forms');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editingPackage]);

  const formName = useCallback(
    (id: string) => allForms.find((f) => f.id === id)?.name ?? '(unknown form)',
    [allForms],
  );

  const toggleForm = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSave = async () => {
    if (!name.trim())        { setSaveError('Package name is required'); return; }
    if (selected.length === 0) { setSaveError('Add at least one form'); return; }
    if (selected.length > MAX_FORMS) { setSaveError(`A package can hold at most ${MAX_FORMS} forms`); return; }

    setSaving(true); setSaveError(null);
    const payload = {
      name: name.trim(),
      description: description || undefined,
      formIds: selected,
      isPublic,
      publicSlug: isPublic && publicSlug ? publicSlug : undefined,
      authMode: isPublic ? authMode : undefined,
      // Full-row replace — always send. NULL = canonical DEFAULT, [] = no step.
      identityFields: isDefaultConfig(identityFields) ? null : identityFields,
    };
    try {
      const res = editingPackage
        ? await packagesApi.update(editingPackage.id, payload)
        : await packagesApi.create(payload);
      if (res.success && res.data) onSaved();
      else setSaveError(res.error ?? 'Save failed');
    } catch {
      setSaveError('Unexpected error saving package');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <h2 style={styles.heading}>{editingPackage ? 'Edit Package' : 'New Package'}</h2>
        <div style={styles.headerActions}>
          <button style={styles.cancelBtn} onClick={onCancel} disabled={saving}>Cancel</button>
          <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Package'}
          </button>
        </div>
      </div>
      {saveError && <div style={styles.errorBanner}>{saveError}</div>}

      <div style={styles.body}>
        {/* Left — package settings */}
        <div style={styles.settingsCol}>
          <div style={styles.field}>
            <label style={styles.label}>Package Name *</label>
            <input style={styles.input} value={name} onChange={(e) => setName(e.target.value)} maxLength={255} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Description</label>
            <textarea style={{ ...styles.input, minHeight: 60 }} value={description} onChange={(e) => setDesc(e.target.value)} maxLength={1000} />
          </div>

          {/* Public toggle */}
          <div style={styles.field}>
            <label style={styles.checkLabel}>
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
              <span>Make package publicly accessible</span>
            </label>
          </div>

          {isPublic && (
            <>
              <div style={styles.field}>
                <label style={styles.label}>URL Slug (leave blank to auto-generate)</label>
                <input
                  style={styles.input}
                  value={publicSlug}
                  onChange={(e) => setSlug(sanitizeSlug(e.target.value))}
                  placeholder="e.g. new-patient-package"
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Authentication</label>
                <select style={styles.select} value={authMode} onChange={(e) => setAuthMode(e.target.value)}>
                  {AUTH_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <p style={styles.hint}>Public packages always require OTP verification.</p>
              </div>
            </>
          )}

          {/* Patient identity fields */}
          <div style={styles.field}>
            <label style={styles.label}>Patient identity fields</label>
            <p style={styles.hint}>Collected once per run (overrides each form&apos;s own identity config).</p>
            <div style={styles.identityList}>
              {IDENTITY_FIELD_CATALOG.map((f) => {
                const included = isIncluded(identityFields, f.key);
                const required = identityFields.find((c) => c.key === f.key)?.required ?? false;
                return (
                  <div key={f.key} style={styles.identityRow}>
                    <label style={styles.identityInclude}>
                      <input
                        type="checkbox"
                        checked={included}
                        onChange={() => setIdentityFields((prev) => toggleField(prev, f.key))}
                      />
                      <span>{f.label}</span>
                    </label>
                    <label style={{ ...styles.identityRequired, opacity: included ? 1 : 0.4 }}>
                      <input
                        type="checkbox"
                        checked={required}
                        disabled={!included}
                        onChange={(e) => setIdentityFields((prev) => setRequired(prev, f.key, e.target.checked))}
                      />
                      <span>Required</span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right — form picker + ordered selection */}
        <div style={styles.pickerCol}>
          <div style={styles.field}>
            <label style={styles.label}>Selected forms ({selected.length})</label>
            {selected.length === 0 ? (
              <p style={styles.muted}>No forms yet. Check forms below to add them in order.</p>
            ) : (
              <div style={styles.selectedList}>
                {selected.map((id, i) => (
                  <div key={id} style={styles.selectedRow}>
                    <span style={styles.stepNum}>{i + 1}</span>
                    <span style={styles.selectedName}>{formName(id)}</span>
                    <div style={styles.reorderBtns}>
                      <button
                        style={styles.iconBtn}
                        onClick={() => setSelected((prev) => moveItem(prev, i, -1))}
                        disabled={i === 0}
                        title="Move up"
                      >↑</button>
                      <button
                        style={styles.iconBtn}
                        onClick={() => setSelected((prev) => moveItem(prev, i, 1))}
                        disabled={i === selected.length - 1}
                        title="Move down"
                      >↓</button>
                      <button
                        style={{ ...styles.iconBtn, color: '#b91c1c' }}
                        onClick={() => setSelected((prev) => removeAt(prev, i))}
                        title="Remove"
                      >✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Form library</label>
            {loading ? (
              <p style={styles.muted}>Loading forms…</p>
            ) : loadError ? (
              <p style={{ color: '#c0392b', fontSize: 13 }}>{loadError}</p>
            ) : allForms.length === 0 ? (
              <p style={styles.muted}>No forms available. Create a form first.</p>
            ) : (
              <div style={styles.pickerList}>
                {allForms.map((f) => (
                  <label key={f.id} style={styles.pickerRow}>
                    <input
                      type="checkbox"
                      checked={selected.includes(f.id)}
                      onChange={() => toggleForm(f.id)}
                    />
                    <span style={styles.pickerName}>{f.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root:        { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 },
  heading:     { margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' },
  headerActions: { display: 'flex', gap: 8 },
  cancelBtn:   { background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#374151' },
  saveBtn:     { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  errorBanner: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '8px 16px', color: '#b91c1c', fontSize: 13, margin: '8px 20px 0' },
  body:        { flex: 1, display: 'flex', gap: 24, overflow: 'hidden', padding: '16px 20px' },
  settingsCol: { width: '45%', maxWidth: 460, overflowY: 'auto' },
  pickerCol:   { flex: 1, overflowY: 'auto' },
  field:       { marginBottom: 16 },
  label:       { display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 },
  input:       { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 13, color: '#111827', boxSizing: 'border-box' as const },
  select:      { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 5, fontSize: 13, color: '#111827' },
  checkLabel:  { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer' },
  hint:        { margin: '5px 0 0', fontSize: 11, color: '#9ca3af', lineHeight: 1.5 },
  identityList: { display: 'flex', flexDirection: 'column', gap: 4, border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, marginTop: 6 },
  identityRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '4px 6px' },
  identityInclude: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer' },
  identityRequired: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b7280', cursor: 'pointer' },
  muted:       { color: '#9ca3af', fontSize: 13, margin: 0 },
  selectedList: { display: 'flex', flexDirection: 'column', gap: 6 },
  selectedRow: { display: 'flex', alignItems: 'center', gap: 8, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px' },
  stepNum:     { width: 22, height: 22, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#eff6ff', color: '#2563eb', borderRadius: 999, fontSize: 12, fontWeight: 600 },
  selectedName: { flex: 1, fontSize: 13, color: '#111827' },
  reorderBtns: { display: 'flex', gap: 4 },
  iconBtn:     { width: 26, height: 26, border: '1px solid #d1d5db', borderRadius: 5, background: '#fff', cursor: 'pointer', fontSize: 13, color: '#374151', lineHeight: 1 },
  pickerList:  { display: 'flex', flexDirection: 'column', gap: 2, border: '1px solid #e5e7eb', borderRadius: 8, padding: 6, maxHeight: 320, overflowY: 'auto' },
  pickerRow:   { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 5, cursor: 'pointer', fontSize: 13, color: '#374151' },
  pickerName:  { flex: 1 },
};
